import { Grid } from '@material-ui/core';
import { Select, TextField, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import { validateComponent } from '../../graphql';
import { DeficiencyComponentRemovalPayload, DeficiencyListModified } from '../../models/models';
import { ComponentRemovalCategories } from '../../rxdb/componentRemovalCategories/queryBuilder';
import { DeficiencyList } from '../../rxdb/deficiencyList/queryBuilder';
import { useNotification } from '../../utilities';
import {
  ComponentRemoval,
  ComponentRemovalCategories as ComponentRemovalCategoriesIds,
} from '../../utilities/enums';
import { removalCategoryIds } from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton';
import useStyles from '../Deficiencies/Deficiencies.styles';

interface Props {
  deficiencyPayloadObj: DeficiencyList;
  deficiencyComponentRemovalPayload: DeficiencyComponentRemovalPayload;
  ComponentRemovalListData: ComponentRemovalCategories[];
  isEditable: boolean;
  validateInputs: () => void;
  setDeficiencyComponentRemovalPayload: React.Dispatch<
    React.SetStateAction<DeficiencyComponentRemovalPayload>
  >;
  setDeficiencyPayload: React.Dispatch<React.SetStateAction<DeficiencyListModified>>;
}

const ComponentRemovalPanel: React.FC<Props> = ({
  deficiencyPayloadObj,
  deficiencyComponentRemovalPayload,
  ComponentRemovalListData,
  isEditable,
  validateInputs,
  setDeficiencyComponentRemovalPayload,
  setDeficiencyPayload,
}) => {
  const classes = useStyles();
  const { errorNotification } = useNotification();
  const deficiencyPayload = deficiencyPayloadObj;
  const [isReasonDuplicate, setIsReasonDuplicate] = useState<boolean>();
  const [parentIdSearch, setParentIdSearch] = useState<string>('');
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [componentRemovalId, setComponentRemovalId] = useState<number | null>(
    deficiencyComponentRemovalPayload?.componentRemovalIdBySC &&
      removalCategoryIds.includes(deficiencyComponentRemovalPayload?.componentRemovalIdBySC)
      ? deficiencyComponentRemovalPayload?.componentRemovalIdBySC
      : deficiencyComponentRemovalPayload?.componentRemovalId,
  );
  const [comments, setComments] = useState<string | null>('');
  const [parentId, setParentId] = useState<number | null>();
  const [parentName, setParentName] = useState<string | null>('');

  useEffect(() => {
    if (
      removalCategoryIds.includes(deficiencyComponentRemovalPayload?.componentRemovalId) ||
      removalCategoryIds.includes(deficiencyComponentRemovalPayload?.componentRemovalIdBySC)
    ) {
      if (
        deficiencyComponentRemovalPayload.componentRemovalId ===
          ComponentRemovalCategoriesIds.DUPLICATED &&
        deficiencyComponentRemovalPayload.componentRemovalIdBySC == null
      ) {
        setParentName(deficiencyComponentRemovalPayload?.parentComponentName);
        setParentId(deficiencyComponentRemovalPayload?.parentComponentId);

        setIsReasonDuplicate(
          deficiencyComponentRemovalPayload.componentRemovalId ===
            ComponentRemovalCategoriesIds.DUPLICATED,
        );
      }
      setComments(
        deficiencyComponentRemovalPayload?.componentRemovalIdBySC &&
          removalCategoryIds.includes(deficiencyComponentRemovalPayload?.componentRemovalIdBySC)
          ? deficiencyComponentRemovalPayload?.commentBySC
          : deficiencyComponentRemovalPayload?.comment,
      );
    }
  }, [deficiencyComponentRemovalPayload]);

  const updateReasonForRemoval = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setComponentRemovalId(Number(value));
    setDeficiencyPayload({ ...deficiencyPayload, isChanged: true });
    const clone = { ...deficiencyComponentRemovalPayload };
    if (clone.componentRemovalIdBySC !== null) {
      clone.comment = clone.commentBySC;
      clone.commentBySC = null;
      clone.componentRemovalIdBySC = null;
    }
    if (Number(value) !== ComponentRemovalCategoriesIds.NONE) {
      const data = ComponentRemovalListData.find(
        item => item?.pkComponentRemovalCategory === Number(value),
      );

      if (data) {
        if (data.name?.toLowerCase() === 'duplicate') setIsReasonDuplicate(true);
        else setIsReasonDuplicate(false);
        if (Object.keys(data)?.length > 0) {
          clone.componentRemovalId = data?.pkComponentRemovalCategory;
        }
      }
      setDeficiencyComponentRemovalPayload({ ...clone, isActive: true, isChanged: true });
    } else {
      setIsReasonDuplicate(false);
      setComments(null);
      setDeficiencyComponentRemovalPayload({
        ...clone,
        isChanged: true,
        isActive: true,
        commentBySC: null,
        componentRemovalIdBySC: null,
        comment: null,
        parentComponentId: null,
        parentComponentName: null,
        componentRemovalId: Number(value),
      });
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;
    const clone = { ...deficiencyComponentRemovalPayload };
    if (!name) return;
    if (name === ComponentRemoval.PARENT_ID_SEARCH) {
      setParentIdSearch(value);
    } else if (name === ComponentRemoval.COMMENT) {
      setComments(value);
      if (clone.componentRemovalIdBySC && !clone.componentRemovalId) {
        clone.componentRemovalId = clone.componentRemovalIdBySC;
        clone.componentRemovalIdBySC = null;
        clone.commentBySC = null;
      }
    }
    clone[name] = value;
    clone.isChanged = true;
    setDeficiencyPayload({ ...deficiencyPayload, isChanged: true });
    setDeficiencyComponentRemovalPayload({ ...clone, isActive: true });
  };

  const validateParentId = async () => {
    try {
      const result = await validateComponent(
        Number(parentIdSearch),
        Number(deficiencyPayload.componentFailureId),
      );
      if (result) {
        if (result.data?.mobileValidateComponentInfo) {
          const { value, display } = result.data.mobileValidateComponentInfo;
          const clone = { ...deficiencyComponentRemovalPayload };
          if (value && display) {
            setParentId(value);
            setParentName(display);
            clone.parentComponentId = value;
            clone.parentComponentName = display;
          }
          setDeficiencyComponentRemovalPayload({ ...clone, isChanged: true });
        }
      } else {
        errorNotification('Not Found');
      }
      setIsValidating(false);
    } catch (error) {
      errorNotification('Not Found');
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    validateInputs();
  }, [deficiencyPayload]);

  return (
    <Grid className={classes.outerGrid} xs={12} item container>
      <Grid container spacing={2}>
        <Grid item container xs={12} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6">Component Removal</Typography>
            <Typography variant="caption">
              <span>
                Fields with<span className={classes.requierd}> *</span> are mandatory
              </span>
            </Typography>
            <hr className={`${classes.breakLine} ${classes.checkboxLabel}`} />
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.componentInfoDisplay}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" className="fieldLabel">
              <span>Reason for removal from planning</span>
            </Typography>
            <Select
              inputProps={{
                'data-testid': 'reason-for-removal-input',
              }}
              name={ComponentRemoval.REASON}
              list={ComponentRemovalListData}
              getOptionLabel={(list: ComponentRemovalCategories) => list?.name}
              getOptionValue={(list: ComponentRemovalCategories) =>
                list?.pkComponentRemovalCategory
              }
              onChange={updateReasonForRemoval}
              placeholder="Select a type"
              disabled={!isEditable}
              value={componentRemovalId}
              defaultValue={componentRemovalId}
              className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
              noMinHeight
            />
          </Grid>
          <Grid item xs={1} />
          {isReasonDuplicate ? (
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>Please enter parent component's ID</span>
              </Typography>
              <TextField
                inputProps={{
                  'data-testid': 'component-removal-search-parent-id',
                }}
                name={ComponentRemoval.PARENT_ID_SEARCH}
                noMinHeight
                size="small"
                onChange={handleChange}
                disabled={!isEditable}
                defaultValue={parentIdSearch}
                value={parentIdSearch}
              />
            </Grid>
          ) : (
            <Grid item xs={6}>
              {componentRemovalId !== ComponentRemovalCategoriesIds.NONE && (
                <>
                  <Typography variant="subtitle2" className="fieldLabel">
                    <span>Comments</span>
                  </Typography>
                  <TextField
                    inputProps={{
                      'data-testid': 'component-removal-comment',
                    }}
                    name={ComponentRemoval.COMMENT}
                    noMinHeight
                    size="small"
                    placeholder="Comments"
                    onChange={handleChange}
                    defaultValue={comments}
                    value={comments}
                    disabled={!isEditable}
                  />
                </>
              )}
            </Grid>
          )}
        </Grid>
        {isReasonDuplicate && (
          <>
            <Grid item xs={12} className={classes.componentInfoDisplay}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className="fieldLabel">
                  <span>Parent Component Id</span>
                </Typography>
                <TextField
                  inputProps={{
                    'data-testid': 'component-removal-parent-id',
                  }}
                  name={ComponentRemoval.PARENT_ID}
                  noMinHeight
                  size="small"
                  placeholder="Parent Component Id"
                  value={parentId}
                  disabled
                />
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={6}>
                <Typography variant="subtitle2" className="fieldLabel">
                  <span>Parent Component Name</span>
                </Typography>
                <TextField
                  inputProps={{
                    'data-testid': 'component-removal-parent-name',
                  }}
                  name={ComponentRemoval.PARENT_NAME}
                  noMinHeight
                  size="small"
                  placeholder="Parent Component Name"
                  value={parentName}
                  disabled
                />
              </Grid>
            </Grid>
            <Grid item xs={12} className={classes.componentInfoDisplay}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className="fieldLabel">
                  <span>Comments</span>
                </Typography>
                <TextField
                  inputProps={{
                    'data-testid': 'component-removal-comment',
                  }}
                  name={ComponentRemoval.COMMENT}
                  noMinHeight
                  size="small"
                  placeholder="Comments"
                  onChange={handleChange}
                  defaultValue={comments}
                  disabled={!isEditable}
                  value={comments}
                />
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={6} container justifyContent="flex-end" alignContent="flex-end">
                <CustomButton
                  buttonText="Validate"
                  isLoading={isValidating}
                  disabled={parentIdSearch.length === 0}
                  onClose={validateParentId}
                  gridStyle={classes.validateButton}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ComponentRemovalPanel;
